import styled, { css, keyframes } from 'styled-components';

/*
 * "Zine" — a neo-brutalist editorial layout: pale lavender canvas, hard ink
 * borders, offset (non-blurred) shadows and oversized display type.
 */
const ink = '#14121f';
const canvas = '#efeafd';
const paper = '#ffffff';
const accent = '#4b2ee6';
const accent2 = '#ff5a3c';
const muted = '#5b5670';
const motion = '0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export const palette = { ink, canvas, paper, accent, accent2, muted };

const hardShadow = (x = 6, y = 6, color = ink) => `${x}px ${y}px 0 ${color}`;

const block = css`
  background: ${paper};
  border: 3px solid ${ink};
  border-radius: 16px;
  box-shadow: ${hardShadow()};
`;

const display = css`
  font-family: 'Inter Tight', Inter, 'Helvetica Neue', Arial, sans-serif;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.05;
  text-transform: uppercase;
`;

const mono = css`
  font-family: ui-monospace, 'SFMono-Regular', 'JetBrains Mono', Menlo, monospace;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

export const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  color: ${ink};
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  line-height: 1.7;
  background-color: ${canvas};
  background-image:
    repeating-linear-gradient(to right, rgba(20, 18, 31, 0.05) 0 1px, transparent 1px 72px),
    repeating-linear-gradient(to bottom, rgba(20, 18, 31, 0.05) 0 1px, transparent 1px 72px);
  scroll-behavior: smooth;

  [id] {
    scroll-margin-top: 7rem;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  :focus-visible {
    outline: 3px solid ${accent2};
    outline-offset: 3px;
  }
`;

export const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem clamp(1.5rem, 5vw, 4rem);
  border-bottom: 3px solid ${ink};
  background: ${canvas};
`;

export const BarBrand = styled.a`
  ${mono};
  display: inline-flex;
  gap: 0.6rem;
  align-items: center;
  max-width: 60vw;
  overflow: hidden;
  color: ${ink};
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::before {
    content: '';
    flex: none;
    width: 0.85rem;
    height: 0.85rem;
    border: 3px solid ${ink};
    border-radius: 50%;
    background: ${accent2};
  }
`;

export const BarNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  @media (max-width: 860px) {
    display: none;
  }
`;

export const BarLink = styled.a`
  ${mono};
  padding-bottom: 0.15rem;
  border-bottom: 3px solid transparent;
  color: ${muted};
  text-decoration: none;
  transition: color ${motion}, border-color ${motion};

  &:hover,
  &:focus-visible {
    border-color: ${accent};
    color: ${ink};
  }
`;

export const BarAction = styled.a`
  ${mono};
  padding: 0.75rem 1.5rem;
  border: 3px solid ${ink};
  border-radius: 999px;
  background: ${accent};
  box-shadow: ${hardShadow(4, 4)};
  color: ${paper};
  text-decoration: none;
  transition: transform ${motion}, box-shadow ${motion};

  &:hover,
  &:focus-visible {
    box-shadow: ${hardShadow(7, 7)};
    transform: translate(-3px, -3px);
  }
`;

export const Shell = styled.main`
  width: min(100% - clamp(2rem, 8vw, 7rem), 1280px);
  margin: 0 auto;
  padding: clamp(3rem, 7vw, 5rem) 0 clamp(4rem, 8vw, 6rem);
`;

export const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: clamp(2rem, 5vw, 4rem);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const Eyebrow = styled.p`
  ${mono};
  display: inline-flex;
  gap: 0.75rem;
  align-items: center;
  margin: 0 0 1.5rem;
  padding: 0.5rem 1.1rem;
  border: 3px solid ${ink};
  border-radius: 999px;
  background: ${paper};
  color: ${ink};
`;

export const HeroName = styled.h1`
  ${display};
  margin: 0;
  font-size: clamp(3rem, 9vw, 7rem);
  overflow-wrap: anywhere;

  span {
    display: inline-block;
    padding: 0 0.15em;
    background: linear-gradient(180deg, transparent 62%, ${accent2}66 62%);
  }
`;

export const HeroBio = styled.p`
  max-width: 46ch;
  margin: 2rem 0 0;
  color: ${muted};
  font-size: 1.15rem;
  line-height: 1.8;
  white-space: pre-line;
`;

export const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2.5rem;
`;

export const ActionLink = styled.a`
  ${mono};
  display: inline-flex;
  gap: 0.6rem;
  align-items: center;
  padding: 0.85rem 1.6rem;
  border: 3px solid ${ink};
  border-radius: 999px;
  background: ${({ $solid }) => ($solid ? ink : paper)};
  box-shadow: ${hardShadow(5, 5)};
  color: ${({ $solid }) => ($solid ? paper : ink)};
  text-decoration: none;
  transition: transform ${motion}, box-shadow ${motion}, background ${motion}, color ${motion};

  &:hover,
  &:focus-visible {
    background: ${({ $solid }) => ($solid ? accent : accent2)};
    box-shadow: ${hardShadow(8, 8)};
    color: ${paper};
    transform: translate(-3px, -3px);
  }
`;

export const HeroCard = styled.div`
  ${block};
  position: relative;
  overflow: hidden;
  padding: 1.25rem;

  &::after {
    content: '';
    position: absolute;
    right: -3.5rem;
    bottom: -3.5rem;
    width: 9rem;
    height: 9rem;
    border: 3px solid ${ink};
    border-radius: 50%;
    background: ${accent}22;
  }
`;

export const HeroPortrait = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 5;
  border: 3px solid ${ink};
  border-radius: 12px;
  object-fit: cover;
`;

export const HeroMonogram = styled.div`
  ${display};
  display: grid;
  place-items: center;
  aspect-ratio: 4 / 5;
  border: 3px solid ${ink};
  border-radius: 12px;
  background: repeating-linear-gradient(45deg, ${accent}1a 0 12px, transparent 12px 24px), ${paper};
  color: ${ink};
  font-size: clamp(3rem, 8vw, 5rem);
`;

export const HeroFacts = styled.dl`
  display: grid;
  gap: 1rem;
  margin: 1.5rem 0 0;
`;

export const Fact = styled.div`
  display: grid;
  gap: 0.2rem;
  padding-top: 1rem;
  border-top: 2px dashed ${ink}33;

  dt {
    ${mono};
    color: ${muted};
  }

  dd {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  a {
    color: ${accent};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

export const Ticker = styled.div`
  overflow: hidden;
  margin: clamp(3rem, 6vw, 4.5rem) 0 0;
  padding: 1.1rem 0;
  border-top: 3px solid ${ink};
  border-bottom: 3px solid ${ink};
  background: ${ink};
  color: ${canvas};
  user-select: none;
`;

export const TickerTrack = styled.div`
  ${display};
  display: flex;
  gap: 2.5rem;
  width: max-content;
  font-size: 1.15rem;
  animation: ${scroll} 32s linear infinite;

  span {
    display: inline-flex;
    gap: 2.5rem;
    align-items: center;
    white-space: nowrap;

    &::after {
      content: '\\002B';
      color: ${accent2};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    width: 100%;
  }
`;

export const Section = styled.section`
  display: grid;
  grid-template-columns: 12rem minmax(0, 1fr);
  gap: clamp(2rem, 4vw, 3.5rem);
  padding: clamp(3.5rem, 7vw, 5.5rem) 0;
  border-bottom: 3px solid ${ink}22;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const SectionLabel = styled.div`
  position: sticky;
  top: 7rem;
  align-self: start;

  h2 {
    ${display};
    margin: 0.75rem 0 0;
    font-size: 1.6rem;
  }

  span {
    ${mono};
    color: ${accent2};
  }

  @media (max-width: 860px) {
    position: static;
  }
`;

export const SectionBody = styled.div`
  display: grid;
  gap: 2rem;
  min-width: 0;
`;

export const RowList = styled.div`
  display: grid;
  gap: 1.5rem;
`;

export const Row = styled.article`
  ${block};
  padding: clamp(1.75rem, 3vw, 2.25rem);
  transition: transform ${motion}, box-shadow ${motion}, background ${motion};

  &:hover {
    background: ${accent}0d;
    box-shadow: ${hardShadow(10, 10)};
    transform: translate(-4px, -4px);
  }
`;

export const RowHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: baseline;
  justify-content: space-between;
`;

export const RowTitle = styled.h3`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

export const RowMeta = styled.p`
  margin: 0.5rem 0 0;
  color: ${muted};
  font-size: 0.95rem;
  font-weight: 600;
`;

export const RowDate = styled.span`
  ${mono};
  flex: none;
  padding: 0.4rem 0.9rem;
  border: 2px solid ${ink};
  border-radius: 999px;
  background: ${canvas};
`;

export const RowCopy = styled.p`
  margin: 1.25rem 0 0;
  color: ${muted};
  line-height: 1.8;
  white-space: pre-line;
`;

export const InlineLink = styled.a`
  ${mono};
  display: inline-flex;
  gap: 0.45rem;
  align-items: center;
  margin-top: 1.25rem;
  padding-bottom: 0.2rem;
  border-bottom: 3px solid ${accent};
  color: ${ink};
  text-decoration: none;
  transition: color ${motion}, border-color ${motion};

  &:hover,
  &:focus-visible {
    border-color: ${accent2};
    color: ${accent};
  }
`;

export const SkillGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
`;

export const SkillChip = styled.button`
  padding: 0.8rem 1.4rem;
  border: 3px solid ${ink};
  border-radius: 999px;
  background: ${paper};
  box-shadow: ${hardShadow(4, 4)};
  color: ${ink};
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: transform ${motion}, box-shadow ${motion}, background ${motion}, color ${motion};

  &:hover,
  &:focus-visible {
    background: ${accent};
    box-shadow: ${hardShadow(7, 7)};
    color: ${paper};
    transform: translate(-3px, -3px);
  }
`;

export const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
`;

export const ProjectCard = styled.article`
  ${block};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform ${motion}, box-shadow ${motion};

  &:hover {
    box-shadow: ${hardShadow(10, 10, accent)};
    transform: translate(-4px, -4px);
  }
`;

export const ProjectCover = styled.div`
  display: grid;
  place-items: center;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-bottom: 3px solid ${ink};
  background: repeating-linear-gradient(135deg, ${accent}1f 0 14px, ${paper} 14px 28px);
  color: ${ink};
  font-weight: 800;
  text-align: center;
  padding: 1rem;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

export const ProjectBody = styled.div`
  display: grid;
  gap: 1rem;
  padding: 2rem;
`;

export const TagRow = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    ${mono};
    padding: 0.3rem 0.75rem;
    border: 2px solid ${ink}33;
    border-radius: 999px;
    color: ${muted};
    font-size: 0.68rem;
  }
`;

export const ProjectLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 0.25rem;
`;

export const ProjectLink = styled.a`
  ${mono};
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  padding-bottom: 0.2rem;
  border-bottom: 3px solid ${ink};
  color: ${ink};
  text-decoration: none;
  transition: color ${motion}, border-color ${motion};

  &:hover,
  &:focus-visible {
    border-color: ${accent2};
    color: ${accent};
  }
`;

export const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
`;

export const ContactWrap = styled.div`
  padding: clamp(3.5rem, 7vw, 5.5rem) 0 0;
`;

export const Footer = styled.footer`
  margin-top: clamp(3rem, 6vw, 4.5rem);
  padding: clamp(3rem, 6vw, 4rem) clamp(1.5rem, 5vw, 4rem) 2rem;
  border-top: 3px solid ${ink};
  background: ${ink};
  color: ${canvas};
`;

export const FooterInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  align-items: flex-end;
  justify-content: space-between;
  width: min(100%, 1280px);
  margin: 0 auto;
`;

export const FooterName = styled.p`
  ${display};
  max-width: 14ch;
  margin: 0;
  font-size: clamp(2rem, 6vw, 3.5rem);
  overflow-wrap: anywhere;
`;

export const FooterLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
`;

export const FooterLink = styled.a`
  display: inline-grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  border: 3px solid ${canvas}55;
  border-radius: 50%;
  color: ${canvas};
  transition: transform ${motion}, background ${motion}, border-color ${motion}, color ${motion};

  &:hover,
  &:focus-visible {
    background: ${accent2};
    border-color: ${accent2};
    color: ${ink};
    transform: translateY(-3px);
  }
`;

export const FooterNote = styled.p`
  ${mono};
  width: min(100%, 1280px);
  margin: 3rem auto 0;
  padding-top: 1.5rem;
  border-top: 2px solid ${canvas}33;
  color: ${canvas}99;
`;
